import React, { useState, useEffect } from 'react';
import { leaderboardApi } from './api/leaderboard';
import { userManager } from '../login/api/auth';
import { FaTrophy, FaMedal, FaAward, FaCrown } from 'react-icons/fa';

const Leaderboard = () => {
  const [selectedDivision, setSelectedDivision] = useState('TPT');
  const [selectedFY, setSelectedFY] = useState('25-26');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Hardcoded financial years
  const financialYears = ['25-26'];

  // Available divisions
  const availableDivisions = [
    { code: 'TPT', name: 'TPT' },
    { code: 'SCM', name: 'SCM' }
  ];

  // Get current user information
  useEffect(() => {
    const user = userManager.getUser();
    setCurrentUser(user);
  }, []);

  // Fetch leaderboard data
  const fetchLeaderboard = async (division, fy) => {
    // Don't fetch if no financial year is selected
    if (!fy) {
      setLeaderboardData([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await leaderboardApi.getLeaderboard(division, fy);
      
      // Add ranks based on sales (backend returns sorted by sales descending)
      const dataWithRanks = data.map((entry, index) => ({
        ...entry,
        rank: index + 1
      }));
      
      setLeaderboardData(dataWithRanks);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError(err.message);
      setLeaderboardData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch leaderboard when division or financial year changes
  useEffect(() => {
    fetchLeaderboard(selectedDivision, selectedFY);
  }, [selectedDivision, selectedFY]);

  const handleDivisionChange = (division) => {
    setSelectedDivision(division);
  };

  const handleFYChange = (fy) => {
    setSelectedFY(fy);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <FaTrophy className="w-6 h-6 text-yellow-500 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]" />;
      case 2:
        return <FaMedal className="w-6 h-6 text-gray-400 drop-shadow-[0_0_8px_rgba(156,163,175,0.5)]" />;
      case 3:
        return <FaAward className="w-6 h-6 text-amber-600 drop-shadow-[0_0_8px_rgba(217,119,6,0.5)]" />;
      default:
        return (
          <div className="w-6 h-6 rounded-full bg-gradient-to-r from-gray-600 to-gray-700 flex items-center justify-center text-xs font-bold text-white">
            {rank}
          </div>
        );
    }
  };

  const getRankGlow = (rank) => {
    switch (rank) {
      case 1:
        return 'ring-2 ring-yellow-500/20 bg-gradient-to-r from-yellow-500/10 to-yellow-600/10';
      case 2:
        return 'ring-2 ring-gray-400/20 bg-gradient-to-r from-gray-400/10 to-gray-500/10';
      case 3:
        return 'ring-2 ring-amber-600/20 bg-gradient-to-r from-amber-600/10 to-amber-700/10';
      default:
        return 'border border-gray-200 hover:border-gray-300 hover:shadow-md';
    }
  };

  const getTextColor = (rank) => {
    switch (rank) {
      case 1:
        return 'text-yellow-600';
      case 2:
        return 'text-gray-600';
      case 3:
        return 'text-amber-600';
      default:
        return 'text-gray-900';
    }
  };

  // Find current user's entry
  const currentUserEntry = leaderboardData.find(
    entry => entry.ECode === currentUser?.ECode
  );

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Sales Leaderboard</h1>
        
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {/* Financial Year Dropdown */}
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Financial Year
              </label>
                             <select
                 value={selectedFY}
                 onChange={(e) => handleFYChange(e.target.value)}
                 className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
               >
                 {financialYears.map(year => (
                   <option key={year} value={year}>
                     {year}
                   </option>
                 ))}
               </select>
            </div>
            
            {/* Division Selector */}
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division
              </label>
              <div className="flex gap-2">
                {availableDivisions.map((division) => (
                  <button
                    key={division.code}
                    onClick={() => handleDivisionChange(division.code)}
                                         disabled={isLoading}
                    className={`
                      px-4 py-2 rounded-lg font-medium transition-all duration-200
                      ${selectedDivision === division.code
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {division.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
                 <div className="flex items-center justify-between">
           <div className="text-sm text-gray-500">
             Top performers • {selectedDivision} Division • FY {selectedFY}
           </div>
          {currentUserEntry && (
            <div className="flex items-center gap-2 text-sm">
              <FaCrown className="w-4 h-4 text-blue-500"/>
              <span className="text-blue-600 font-medium">
                Your rank: #{currentUserEntry.rank}
              </span>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={index} className="animate-pulse flex items-center justify-between p-4 rounded-lg bg-white border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-6 h-6 bg-gray-300 rounded" />
                <div className="flex flex-col gap-2">
                  <div className="h-4 bg-gray-300 rounded w-32" />
                  <div className="h-3 bg-gray-300 rounded w-20" />
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="h-4 bg-gray-300 rounded w-16" />
                <div className="h-3 bg-gray-300 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <div className="mb-4">
            <FaTrophy className="w-12 h-12 text-red-500 mx-auto" />
          </div>
          <p className="text-red-600 font-medium">Failed to load leaderboard</p>
          <p className="text-sm text-gray-500">Please try again later</p>
          <button
            onClick={() => fetchLeaderboard(selectedDivision, selectedFY)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
             ) : leaderboardData.length ? (
        <div className="space-y-3">
          {leaderboardData.map((entry) => {
            const isCurrentUser = entry.ECode === currentUser?.ECode;
            
            return (
              <div
                key={entry.ECode}
                className={`
                  flex items-center justify-between p-4 rounded-lg
                  bg-white
                  ${getRankGlow(entry.rank)}
                  transition-all duration-200 transform hover:scale-[1.02]
                  shadow-sm
                `}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center transition-transform duration-200 hover:scale-110">
                    {getRankIcon(entry.rank)}
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className={`
                        font-semibold text-base hover:text-blue-600 transition-colors
                        ${getTextColor(entry.rank)}
                      `}>
                        {entry.name}
                      </span>
                      {isCurrentUser && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-gray-600 rounded-full font-medium">
                          You
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>ECode: {entry.ECode}</span>
                      <span>Rank #{entry.rank}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="font-mono text-lg font-bold text-gray-900">
                        ₹{entry.sales.toLocaleString("en-IN")}
                      </div>
                      <div className="text-xs text-gray-500">Sales (in lakhs)</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-lg font-bold text-green-600">
                        {entry.deals_won}
                      </div>
                      <div className="text-xs text-gray-500">Deals Won</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="mb-4">
            <FaTrophy className="w-12 h-12 text-gray-400 mx-auto" />
          </div>
          <p className="text-gray-600 font-medium">No leaderboard data available</p>
          <p className="text-sm text-gray-500">Be the first to win deals!</p>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
